import { useMemo, useEffect, PropsWithChildren } from 'react';
import { ApolloProvider } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from 'next/router';
import { makeClient } from '@updater/ui-utilities';
import { useAuthMigrationSignOut } from 'context/auth';
import { useServerProps } from 'utils/ServerProps';
import { isGuestPathname } from 'utils/routes';
import { useSession } from '@updater/auth';
import { makeGuestApolloClient } from 'context/guest-auth';

type OnErrorParams = Parameters<typeof onError>[0];
type NetworkError = Parameters<OnErrorParams>[0]['networkError'];

const isUnauthorizedNetworkError = (networkError?: NetworkError) => {
  // https://github.com/apollographql/apollo-link/issues/300#issuecomment-518445337
  return (
    networkError &&
    'statusCode' in networkError &&
    (networkError.statusCode === 401 || networkError.statusCode === 403)
  );
};

export const ApolloClientSetup: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const { session } = useSession();
  const signOut = useAuthMigrationSignOut();
  const router = useRouter();
  // Guest routes must remain anonymous even if the browser still holds a
  // moving-app session — passing those credentials through causes the backend
  // to reject guest mutations and bounces the user to the error page.
  // Guest routes run on their own Apollo client, authenticated from the
  // isolated guest auth store — see context/guest-auth. The strip below is
  // therefore belt-and-braces for the signed-in client: it should never be
  // handed guest traffic in the first place.
  const isGuestRoute = isGuestPathname(router?.pathname);
  const effectiveSession = isGuestRoute ? undefined : session;

  const { apiBaseUrl } = useServerProps();
  if (!apiBaseUrl) {
    datadogRum.addError(new Error('Missing apiBaseUrl'));
  }

  const logoutLink = useMemo(() => {
    return onError(({ graphQLErrors, networkError, operation }) => {
      /**
       * Verifies that the http request is not a 401 or 403
       */
      if (isUnauthorizedNetworkError(networkError)) {
        console.warn(
          'UNAUTHORIZED NETWORK RESPONSE! Query:',
          operation.operationName
        );
        datadogRum.addError(
          new Error(
            `UNAUTHORIZED NETWORK RESPONSE FOR QUERY ${operation?.operationName}`
          ),
          {
            ...operation,
          }
        );
        // TODO: instrument this as a notification — such as a toast
        // 'Oops, something went wrong on our end! Please log back in to try again.'
        signOut();
      }
      // mutates networkError so datadog may distinguish each graph request
      if (networkError) {
        const definitionNode = operation.query.definitions?.[0];
        networkError.cause = {
          graphQLOperationName: operation.operationName,
          graphQLOperationType:
            'operation' in definitionNode
              ? definitionNode.operation
              : undefined,
        } as unknown;
      }

      /**
       * End session when GraphQL returns UNAUTHENTICATED code in its
       * response body at:
       *  { errors: [{ extensions: { code: "UNAUTHENTICATED"} }] }
       */
      if (
        graphQLErrors?.some(
          (error) => error.extensions?.code === 'UNAUTHENTICATED'
        )
      ) {
        signOut();
      }
    });
  }, [signOut]);

  const apolloClient = useMemo(() => {
    return makeClient({
      requestHeaders: () => ({
        name: 'consumer-app',
        version: '0.1',
        app: 'mover',
        ...effectiveSession,
      }),
      url: `${apiBaseUrl}/graphql`,
      links: effectiveSession ? [logoutLink] : [],
      name: 'consumer-app',
      // eslint-disable-next-line no-underscore-dangle
      version: global.__VERSION_STRING__,
    });
  }, [effectiveSession, apiBaseUrl, logoutLink]);

  // Built unconditionally so the hook order is stable across a guest/signed-in
  // navigation, and memoized on apiBaseUrl alone so it survives a guest session
  // being minted mid flow.
  const guestApolloClient = useMemo(() => {
    return makeGuestApolloClient({
      apiBaseUrl,
      // eslint-disable-next-line no-underscore-dangle
      version: global.__VERSION_STRING__,
    });
  }, [apiBaseUrl]);

  const activeClient = isGuestRoute ? guestApolloClient : apolloClient;

  useEffect(() => {
    const unsubscribe = activeClient.onResetStore(async () => {
      datadogRum.addAction('apollo:storeReset', {
        uid: session?.uid,
      });
    });
    return unsubscribe;
  }, [activeClient]);

  return <ApolloProvider client={activeClient}>{children}</ApolloProvider>;
};
